import { useMemo } from "react";
import { Box } from "@mui/material";
import { useStudentReportContext } from "../../../../../context/StudentReportContext";
import { DateView } from "../../../../../components/DateView/DateView";
import { SERVER_KEY } from "../../../../../config/constants/EnviormentVariables";

const useStudentsReports = () => {
  const { studentReports } = useStudentReportContext();

  const REPORT_MAPPING = {
    training_objective_achieved:
      "Did you achieve your objectives by joining the training?",
    recommend_consulnet:
      "Do you recommend ConsulNet Corporation to other people?",
    training_impact_personal_life:
      "What was the impact of training on your personal life? (select all that apply)",
    post_training_activity:
      "What did you do after graduating from the training?",
    future_plans: "What do you plan to do in the future?",
    employment_status:
      "Have you ever been employed/ freelanced / run a business?",
    unemployment_reasons: "Reason(s) for not working",
    number_of_jobs: "Number of jobs since completing the training",
    employment_relevance:
      "Relevance of employment to skills learnt at ConsulNet",
    total_experience_years: "Total years of professional experience",
    tech_experience_years: "Years of experience in tech industry",
    career_without_training:
      "I would have had a career without the training too.",
    pay_better_than_before:
      "Is your current pay better than before you got the training? (Yes/No)",
    pay_better_reason: "If yes, do you think it is because of the training?",
    freelancing_platforms: "Platforms used",
    freelancing_projects: "Share links of Projects completed",
    freelancing_income: "Average monthly income",
    freelancing_technologies: "Technologies used",
    freelancing_help_from_training:
      "Did your training at ConsulNet help in freelancing journey?",
    business_name: "Business Name",
    business_nature: "Nature of business",
    business_sales_platforms: "Sales platforms used",
    business_tech_enabled: "Tech-enabled? (Yes/No)",
    business_duration: "Duration (years)",
    business_monthly_revenue: "Monthly revenue",
    engagement_feature_journey: "Would you like your journey to be featured?",
    engagement_community_roles:
      "Would you like to engage with the CodeGirls community as:",
    expect_learn: "What do you expect to learn from the program?",
    join_reason: "Why do you want to join this training?",
    join_reasons: "Why do you want to join this training?",

    // Pre-assessment explicit labels
    registration_number: "Registration Number",
    course_name: "Course Name",
    cohort_name: "Cohort",
    section_name: "Cohort Section",
    registration_date: "Registration Date",
    shift_days: "Shift Day",
    shift_time: "Shift Time",
    cnic: "CNIC",
    residential_area: "Residential Area",
    dateof_birth: "Date of Birth",
    marital_status: "Marital Status",
    father_name: "Father Name",
    father_profession: "What Is Your Father's Profession?",
    mother_profession: "What Is Your Mother's Profession?",
    sibling: "How Many Siblings Do You Have?",
    siblings_education_numbers:
      "How Many Of Your Siblings Are Getting An Education?",
    husband_name: "What Is Your Husband's Name?",
    husband_profession: "What Is Your Husband's Profession?",
    number_ofkids: "How Many Kids Do You Have?",
    kids_education_numbers: "How Many Of Your Kids Are Getting An Education?",
    number_of_family_members: "Number of Family Members",
    number_of_employed_family_members: "Number of Employed Family Members",
    average_income: "What Is Your Average Household Income Per Month?",
    highest_qualifications: "What Is Your Highest Academic Qualification?",
    other_qualifications: "Other Qualifications",
    additional_diplomas_certifications:
      "Have You Earned Any Other Diploma / Certifications?",
    diplomas_certifications_details:
      "Please Give Your Diploma / Certifications Details",
    professional_experience: "Do You Have Prior Professional Experience?",
    experience_years: "How Many Years Of Professional Experience?",
    currently_employed: "Are You Currently Employed?",
    position: "Please Add The Position",
    salary: "What Is Your Salary?",
    employer_name: "Your Employer Name?",
    tech_training: "Do You Have Any Prior Tech Training Or Education?",
    tech_training_details: "Please Give Your Tech Training Details",
    institutes: "Institute Name",
    household_items: "Which Of The Following Do You Have In Your Household?",
    laptop_count: "Laptops Count",
    desktop_count: "Desktop Count",
    smartphone_count: "Smartphone Count",
    internet_count: "Internet Count",
    laptopdesktop: "Do You Own A Laptop/Desktop?",
    smartphone: "Do You Own A Smartphone?",
    usually_commute: "How Do You Usually Commute?",
    usually_commute_other: "How Do You Usually Commute? (Other)",
    program_commute: "How Do You Commute For This Program?",
    program_commute_other: "How Do You Commute For This Program? (Other)",
    student_skills: "Do You Have Any Of The Following Skills?",
    coding_language: "Coding / Programming Language",
    spoken_english_rating:
      "Rate your Spoken English Communication Skills (1 poor - 5 best)",
    written_english_rating:
      "Rate your Written English Communication Skills (1 poor - 5 best)",
    join_reason_other: "Why Do You Want To Join This Training? (Other)",
    student_status: "Student Status",
    flag_student: "Flag Status",
    flag_reason: "Flag Reason",
  };

  // Explicit ordered sequence — matches the pre-assessment form top to bottom
  const PRE_ASSESSMENT_ORDERED_KEYS = [
    // ── Personal Info ──────────────────────────────────────────────────
    "SN",
    "Name",
    "Email",
    "cnic",
    "residential_area",
    "address",
    "phone",
    "emergency_number",
    "dateof_birth",
    "marital_status",

    // ── Identity / Enrollment ──────────────────────────────────────────
    "registration_number",
    "course_name", // course the student is enrolled in
    "cohort_name",
    "section_name",
    "registration_date",
    "shift_days",
    "shift_time",

    // ── Family (Single) ────────────────────────────────────────────────
    "father_name",
    "father_profession",
    "mother_profession",
    "sibling", // number_of_siblings
    "siblings_education_numbers",

    // ── Family (Married / Divorced / Widow) ───────────────────────────
    "husband_name",
    "husband_profession",
    "number_ofkids", // number_of_kids
    "kids_education_numbers",

    // ── Household ─────────────────────────────────────────────────────
    "number_of_family_members",
    "number_of_employed_family_members",
    "average_income",

    // ── Education ─────────────────────────────────────────────────────
    "highest_qualifications",
    "other_qualifications",
    "additional_diplomas_certifications",
    "diplomas_certifications_details",

    // ── Professional Experience ────────────────────────────────────────
    "professional_experience",
    "experience_years",
    "currently_employed",
    "position",
    "salary",
    "employer_name",

    // ── Tech Training ─────────────────────────────────────────────────
    "tech_training",
    "tech_training_details",
    "institutes", // tech_training_details institute filter

    // ── Tech Assets ───────────────────────────────────────────────────
    "household_items",
    "laptop_count",
    "desktop_count",
    "smartphone_count",
    "internet_count",
    "laptopdesktop",
    "smartphone",

    // ── Commute ───────────────────────────────────────────────────────
    "usually_commute",
    "usually_commute_other",
    "program_commute",
    "program_commute_other",

    // ── Skills & Language ─────────────────────────────────────────────
    "student_skills",
    "coding_language", // specified when "Coding / Programming" is selected
    "spoken_english_rating",
    "written_english_rating",

    // ── Motivation ────────────────────────────────────────────────────
    "join_reason",
    "join_reason_other", // filled when "Other" is selected
    "expect_learn",

    // ── Status flags (admin-side) ─────────────────────────────────────
    "student_status",
    "flag_student",
    "flag_reason",
  ];

  // Explicit ordered sequence — matches the post-assessment form top to bottom
  const POST_ASSESSMENT_ORDERED_KEYS = [
    // ── Outcomes & Impact ─────────────────────────────────────────────
    "training_objective_achieved",
    "recommend_consulnet",
    "training_impact_personal_life",
    "post_training_activity",
    "future_plans",

    // ── Employment ────────────────────────────────────────────────────
    "employment_status",
    "unemployment_reasons",
    "number_of_jobs",
    "employment_relevance",
    "total_experience_years",
    "tech_experience_years",
    "career_without_training",
    "pay_better_than_before",
    "pay_better_reason",

    // ── Freelancing ───────────────────────────────────────────────────
    "freelancing_platforms",
    "freelancing_projects",
    "freelancing_income",
    "freelancing_technologies",
    "freelancing_help_from_training",

    // ── Business ──────────────────────────────────────────────────────
    "business_name",
    "business_nature",
    "business_sales_platforms",
    "business_tech_enabled",
    "business_duration",
    "business_monthly_revenue",

    // ── Community Engagement ──────────────────────────────────────────
    "engagement_feature_journey",
    "engagement_community_roles",
  ];

  // Master sequence: pre-assessment fields first, then post-assessment fields.
  const MASTER_ORDERED_KEYS = [
    ...PRE_ASSESSMENT_ORDERED_KEYS,
    ...POST_ASSESSMENT_ORDERED_KEYS,
  ];

  // Normalize a key to snake_case lowercase so ordering survives any
  // casing / spacing differences coming from the API.
  const normalizeKey = (key) => key.toLowerCase().replace(/\s+/g, "_");

  const formatHeader = (key) => {
    const normalizedKey = key.toLowerCase().replace(/\s+/g, "_");
    if (REPORT_MAPPING[normalizedKey]) {
      return REPORT_MAPPING[normalizedKey];
    }
    return key.replace(/_/g, " ").replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
    });
  };

  const { columns, modifiedData, csvData } = useMemo(() => {
    if (!Array.isArray(studentReports) || studentReports.length === 0) {
      return { columns: [], modifiedData: [], csvData: [] };
    }

    // Remove exact duplicate entries
    const uniqueReports = studentReports.filter(
      (item, index, self) =>
        index ===
        self.findIndex((t) => JSON.stringify(t) === JSON.stringify(item)),
    );

    // Add SN as row number and keep name/email first
    const modifiedData = uniqueReports.map((item, index) => {
      const { id, name, email, ...rest } = item;
      return {
        SN: `${(index + 1).toString().padStart(3, "0")}`,
        Name: name,
        Email: email,
        ...rest,
      };
    });

    // Build ordered keys: follow MASTER_ORDERED_KEYS (pre → post) exactly,
    // then append any remaining dynamic keys not covered by the list at the end.
    const allDataKeys = Array.from(new Set(modifiedData.flatMap(Object.keys)));

    // Map normalized data key -> actual data key so the ordered list can match
    // regardless of casing / spacing differences coming from the API.
    const dataKeyByNormalized = new Map(
      allDataKeys.map((key) => [normalizeKey(key), key]),
    );

    // Keys that are in our master ordered list AND actually present in the data
    const usedDataKeys = new Set();
    const orderedKeys = [];
    MASTER_ORDERED_KEYS.forEach((key) => {
      const actualKey = dataKeyByNormalized.get(normalizeKey(key));
      if (actualKey && !usedDataKeys.has(actualKey)) {
        orderedKeys.push(actualKey);
        usedDataKeys.add(actualKey);
      }
    });

    // Any remaining keys not covered by the ordered list
    const remainingKeys = allDataKeys.filter((key) => !usedDataKeys.has(key));

    const allKeys = [...orderedKeys, ...remainingKeys];

    const columns = allKeys.map((key) => {
      const normalizedKey = key.toLowerCase().replace(/\s+/g, "_");
      const isStudentSkillsColumn =
        normalizedKey === "student_skills" || normalizedKey.includes("skills");
      const isCourseHistoryColumn = normalizedKey === "course_history";

      return {
        name: formatHeader(key),
        selector: (row) =>
          isCourseHistoryColumn && row[key] ? (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                padding: "6px 0",
              }}
            >
              {String(row[key])
                .split(",")
                .map((course) => course.trim())
                .filter(Boolean)
                .map((course, idx) => (
                  <Box
                    key={idx}
                    component="span"
                    sx={{
                      display: "inline-block",
                      border: "1px solid #b9d3e0",
                      borderRadius: "9999px",
                      backgroundColor: "#f3f8fb",
                      color: "#022d40",
                      padding: "6px 14px",
                      fontSize: "13px",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {course}
                  </Box>
                ))}
            </Box>
          ) : key.toLowerCase().includes("date") && row[key] ? (
            <DateView date={row[key]} />
          ) : key.toLowerCase().includes("resume") && row[key] ? (
            <a
              href={`${SERVER_KEY}${row[key]}`}
              download
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#022d40", textDecoration: "underline" }}
            >
              Download Resume
            </a>
          ) : (
            row[key]
          ),
        sortable: true,
        // Columns whose selector renders JSX (course history, dates, resume)
        // can't be sorted by the rendered element — sort by the raw value.
        sortFunction: (a, b) => {
          const aVal = a[key] ?? "";
          const bVal = b[key] ?? "";
          return String(aVal).localeCompare(String(bVal), undefined, {
            numeric: true,
            sensitivity: "base",
          });
        },
        wrap: true,
        minWidth: isStudentSkillsColumn || isCourseHistoryColumn ? "380px" : "200px",
        grow: isStudentSkillsColumn || isCourseHistoryColumn ? 2 : 1,
      };
    });

    // CSV export — rows are built in the same allKeys order so the
    // downloaded file columns match exactly what is shown on screen.
    const csvData = modifiedData.map((item) => {
      const orderedRow = {};
      allKeys.forEach((key) => {
        const header = formatHeader(key);
        let value =
          item[key] !== undefined && item[key] !== null ? item[key] : "";
        // Prefix resume paths with the server URL
        if (key.toLowerCase().includes("resume") && value) {
          value = `${SERVER_KEY}${value}`;
        }
        orderedRow[header] = value;
      });
      return orderedRow;
    });

    return { columns, modifiedData, csvData };
  }, [studentReports]);

  return { columns, modifiedData, csvData };
};

export default useStudentsReports;
